import { Box } from '@mui/material';
import { ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useMatch } from 'react-router-dom';

import {
    getCatalogItems,
    getCatalogItemsAreLoading,
} from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { getSelectCategoryId } from 'entities/Category/model/selectors/categorySelectors';
import { addCatalogItemActions } from 'features/AddCatalogItem/model/slices/AddCatalogItemSlice';
import {
    getRouteCatalogInventoryCategories,
    getRouteGlobalCatalog,
} from 'shared/const/router';
import {
    useIsAddItemsFlow,
    useIsOrdersAddItemsMarketplace,
} from 'shared/lib/contexts/CatalogIdentityContext';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useInitialEffect } from 'shared/lib/hooks/useInitialEffect';
import { useUnmountEffect } from 'shared/lib/hooks/useUnmountEffect';

import {
    PropertyCatalogCategoryList,
    PropertyCatalogItems,
    PropertyCatalogItemsMoveTo,
} from '..';

import type { CatalogCategoryChangeFollowUp } from 'features/changeCatalogItemCategory';

interface PropertyCatalogProps {
    isEditing?: boolean;
    moveTo: PropertyCatalogItemsMoveTo;
    actionButtons?: ReactNode;
    onLeave?: () => void;
    onAfterCatalogCategoryChange?: CatalogCategoryChangeFollowUp;
}

export const PropertyCatalog = ({
    isEditing,
    actionButtons,
    moveTo,
    onLeave,
    onAfterCatalogCategoryChange,
}: PropertyCatalogProps) => {
    const itemsAreLoading = useSelector(getCatalogItemsAreLoading);
    const selectedCategoryId = useSelector(getSelectCategoryId);
    const catalogItems = useSelector(getCatalogItems);
    const dispatch = useAppDispatch();
    const location = useLocation();

    const isGlobalCatalog = useMatch(
        getRouteGlobalCatalog(':propertyId') + '/*',
    );
    const isOrderMarketplace = useIsOrdersAddItemsMarketplace();
    const isGlobalCatalogMode = Boolean(isGlobalCatalog || isOrderMarketplace);

    const isAddPropertiesCatalogInventoryItems = useMatch(
        getRouteCatalogInventoryCategories(':propertyId') + '/*',
    );

    const isCatalogSection = location.pathname.startsWith('/catalog');
    const isAddItemsFlow = useIsAddItemsFlow();

    const useFlexFillHeight =
        (isCatalogSection && !isEditing) || isAddItemsFlow;

    useInitialEffect(() => {
        if (isGlobalCatalog) {
            dispatch(addCatalogItemActions.setEditItemMode(true));
        }
    });

    useUnmountEffect(() => {
        if (onLeave) {
            onLeave();
        }
    });

    const showCatalogItems = !(
        !selectedCategoryId ||
        (catalogItems?.length === 0 && !itemsAreLoading)
    );

    const screenHeight = useMemo(
        () =>
            isAddPropertiesCatalogInventoryItems || isGlobalCatalog
                ? '332px'
                : '385px',
        [isAddPropertiesCatalogInventoryItems, isGlobalCatalog],
    );

    const heightOfListForEditMode = useMemo(
        () =>
            isEditing ? `calc(100vh - ${screenHeight})` : `calc(100vh - 353px)`,
        [isEditing, screenHeight],
    );

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flex: 1,
                minHeight: 0,
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px',
                borderBottomLeftRadius: isAddItemsFlow ? 0 : '12px',
                overflow: 'hidden',
                ...(useFlexFillHeight
                    ? { height: '100%' }
                    : { maxHeight: heightOfListForEditMode }),
            }}
        >
            <PropertyCatalogCategoryList
                isEditing={isEditing}
                isGlobal={isGlobalCatalogMode}
                moveTo={moveTo}
                fillHeight={useFlexFillHeight}
            />
            <Box
                flexBasis="50%"
                borderRadius="0 12px 0 0"
                border="1px solid"
                borderColor="grey.500"
                display="flex"
                flexDirection="column"
                style={{ flex: 1 }}
                minHeight="0"
                sx={
                    isAddItemsFlow
                        ? {
                              borderBottom: 'none',
                          }
                        : undefined
                }
            >
                <PropertyCatalogItems
                    moveTo={moveTo}
                    showCatalogItems={showCatalogItems}
                    actionButtons={actionButtons}
                    onAfterCatalogCategoryChange={onAfterCatalogCategoryChange}
                />
            </Box>
        </Box>
    );
};
